import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import type { Item } from '../../../models';
import { FuelEntityType, FuelPortalService } from '../../../services';
import { IngestService } from '../../../services/ingest-service';
import type { RootState } from '..';
import { SetSnackbarMessage } from '../application-data';
import { SaveAssetJob } from './SaveAssetJob';
import type { StartJobPayload } from './SaveAssetJobsState';
import { removeJob, updateJob } from './SaveAssetJobsState';
import { uploadFile } from './uploadFile';

export const processSaveExternalSubtitleJob = createAsyncThunk(
  'saveAssetJobs/processSaveExternalSubtitleJob',
  async (payload: StartJobPayload, { dispatch, getState }) => {
    // console.log('processSaveExternalSubtitleJob', { payload });
    let toReturn: Item = null;
    const { form, jobType, extraParams } = payload;
    const state = getState() as RootState;
    const job = new SaveAssetJob(form, jobType, extraParams);
    const { categories } = state.applicationData;
    const { schemas } = state.applicationData;
    const { fuelUserAccessData } = state.fuelUserAccess;
    const { ouData } = state.applicationData;
    // console.log('processSaveExternalSubtitleJob', { job, schemas, categories });
    const assetId = form.originalAssetData.id;
    let uploadUrls = null;

    let languageCode: string | undefined = '';
    if (jobType === 'Subtitle') {
      if (extraParams) {
        languageCode = extraParams.languageCode;
      }
      if (!languageCode) {
        console.error('no language code provided', { payload });
        throw '[processSaveExternalSubtitleJob] No language code provided';
      }
    }

    // console.log('processSaveExternalSubtitleJob', { languageCode });

    const updateMessage = (message: string) => {
      // console.log('message', message);
      job.message = message;
      dispatch(updateJob(job));
    };

    const getUrl = (uploadUrls: any) => {
      const url = '';
      if (jobType === 'Subtitle') {
        return uploadUrls.externalSubtitleS3UploadUrl;
      }
      if (jobType === 'Thumbnail') {
        return uploadUrls.externalThumbnailS3UploadUrl;
      }
      return url;
    };

    const getExtension = (str) => str.substr(str.lastIndexOf('.') + 1);

    const getExternalSubtitleObj = (uploadUrls: any, extension) => {
      return {
        description: 'en-US', // TODO: Pass in language code
        fileExtension: extension, // TODO: Grab from filename
        publicUrl: null,
        s3Path: uploadUrls.externalSubtitleFileUrl,
      };
    };
    const getExtensionText = () => {
      if (jobType === 'Subtitle') {
        return 'subtitleext';
      }
      if (jobType === 'Thumbnail') {
        return 'thumbnailext';
      }
      return null;
    };
    let extension = null;
    const extensionText = getExtensionText();

    // console.log('extension', { extension });

    const file = job.assetForm.ingestFile;
    if (file) {
      extension = getExtension(file.name);
    }
    job.progress = 0;
    try {
      updateMessage(`Preparing for upload...`);
      uploadUrls = await IngestService.getUploadUrl(assetId, FuelEntityType.Clip, extensionText, extension);
      // console.log('uploadUrls', uploadUrls);
      updateMessage(`Uploading file...`);
      const url = getUrl(uploadUrls);
      await uploadFile(file, url, (progress) => {
        job.progress = progress;
      });
    } catch (err) {
      updateMessage(`There was an error uploading file.`);
      throw err;
    }

    const asset = await FuelPortalService.getClipById(assetId);
    if (asset && asset.data && jobType === 'Subtitle') {
      if (asset.data.externalSubtitles == null) {
        asset.data.externalSubtitles = [];
      }

      const newSubtitle = {
        description: extraParams.languageCode,
        publicUrl: null,
        previewUri: null,
        s3Path: uploadUrls.externalSubtitleFileUrl,
        fileExtension: extension,
      };

      const foundIndex = _.findIndex(
        asset.data.externalSubtitles,
        (s) => s.description.toLowerCase() === extraParams.languageCode?.toLowerCase()
      );
      if (foundIndex > -1) {
        asset.data.externalSubtitles[foundIndex] = newSubtitle;
      } else {
        asset.data.externalSubtitles.push(newSubtitle);
      }
      // asset.data.externalSubtitles.forEach((item) => {
      //   item.s3Path = uploadUrls.externalSubtitleFileUrl;
      //   item.publicUrl = null;
      //   item.previewUri = null;
      // });

      updateMessage(`Updating asset...`);
      // multiple Item type signatures running around, i believe they are compatible though...
      toReturn = await FuelPortalService.updateAsset(asset as Item);
    } else if (asset && asset.data && asset.data.thumbnail && uploadUrls && jobType === 'Thumbnail') {
      // asset.data.externalSubtitles = externalSubtitles;
      asset.data.thumbnail.s3Path = uploadUrls.externalThumbnailFileUrl;
      asset.data.thumbnail.publicUrl = null;
      asset.data.thumbnail.previewUri = null;
      updateMessage(`Updating asset...`);
      // Updating asset with allowRestricted true for only thumbnail
      const restrictedFields = ['Metadata.Thumbnail'];
      toReturn = await FuelPortalService.updateAsset(asset as Item, restrictedFields);
    }

    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    dispatch(
      SetSnackbarMessage({
        message: 'File successfully uploaded.',
        severity: 'success',
      })
    );
    return toReturn;
  }
);
